import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Github, Package, CheckCircle2, XCircle, Clock, Zap } from 'lucide-react';
import LabNavigation from '@/components/LabNavigation';

interface GitHubStats {
  stars: number;
  forks: number;
  watchers: number;
  openIssues: number;
}

interface TimelineEvent {
  date: string;
  title: string;
  description: string;
  highlight?: boolean;
}

const timelineEvents: TimelineEvent[] = [
  { date: "Nov 2024", title: "MCP Launches", description: "Anthropic open-sources Model Context Protocol", highlight: true },
  { date: "Dec 2024", title: "Ecosystem Explosion", description: "1000+ open-source connectors emerge" },
  { date: "Jan 2025", title: "The Gap Emerges", description: "Developers struggle to test MCP servers reliably" },
  { date: "Feb 2025", title: "mcp-jest Born", description: "First Jest-like testing framework for MCP", highlight: true },
  { date: "Mar 2025", title: "OpenAI Adopts MCP", description: "ChatGPT, Agents SDK integrate the protocol" },
  { date: "Apr 2025", title: "Google Confirms", description: "DeepMind announces Gemini MCP support" },
  { date: "Dec 2025", title: "Linux Foundation", description: "MCP donated to Agentic AI Foundation" },
];

const competitorData = [
  { name: "MCP Inspector", focus: "Debugging", limitation: "No automation, manual only", icon: "🔍" },
  { name: "Postman", focus: "HTTP APIs", limitation: "No MCP semantics", icon: "📮" },
  { name: "JMeter", focus: "Load testing", limitation: "Not for functional tests", icon: "⚡" },
  { name: "FastMCP Client", focus: "Unit tests", limitation: "Limited scope", icon: "🧪" },
  { name: "Testomat.io", focus: "Enterprise QA", limitation: "Overkill for most", icon: "🏢" },
];

const McpJestCaseStudy = () => {
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTimeline, setActiveTimeline] = useState<number | null>(null);
  const [githubStats, setGithubStats] = useState<GitHubStats | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateScrollAndProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setReadingProgress(Math.min(100, Math.max(0, progress)));
      setScrollY(scrollTop);
    };

    window.addEventListener('scroll', updateScrollAndProgress);
    setTimeout(() => setIsVisible(true), 300);

    return () => window.removeEventListener('scroll', updateScrollAndProgress);
  }, []);

  useEffect(() => {
    // Fetch GitHub stats
    fetch('https://api.github.com/repos/josharsh/mcp-jest')
      .then(res => res.json())
      .then(data => {
        setGithubStats({
          stars: data.stargazers_count || 708,
          forks: data.forks_count || 45,
          watchers: data.watchers_count || 708,
          openIssues: data.open_issues_count || 12,
        });
      })
      .catch(() => {
        // Fallback stats
        setGithubStats({ stars: 708, forks: 45, watchers: 708, openIssues: 12 });
      });
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#fdfaf5]">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className="h-0.5 bg-gradient-to-r from-zinc-800 via-zinc-600 to-zinc-800 transition-all duration-300 ease-out"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-40 group flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm font-inter">Back</span>
      </motion.button>

      <div
        ref={contentRef}
        className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Hero Section */}
        <section className="min-h-[80vh] flex flex-col justify-center items-center px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block px-4 py-2 bg-zinc-100 rounded-full text-xs font-inter uppercase tracking-wider text-zinc-600 mb-8">
              Deep Dive / Lab
            </div>

            <h1 className="font-inter font-black text-4xl md:text-6xl lg:text-7xl tracking-tight text-zinc-900 mb-6 leading-tight">
              The First Jest-Like Testing Framework for MCP Servers
            </h1>

            <p className="font-lora text-xl md:text-2xl text-zinc-600 mb-12 italic">
              "Because even AI needs reliable tests"
            </p>

            {/* Stats Strip */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-12 text-sm font-inter">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-700">500+ weekly downloads</span>
              </div>
              <div className="flex items-center gap-2">
                <Github className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-700">MIT Licensed</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-700">Any transport</span>
              </div>
            </div>

            {/* Animated Test Result */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-16 bg-zinc-900 rounded-lg p-6 text-left font-mono text-sm max-w-xl mx-auto"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-zinc-500 text-xs">mcp-jest</span>
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <p className="text-zinc-400">$ mcp-jest tests/server.json</p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="text-green-400 mt-2"
                >
                  PASS  MCP Server Tests
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="mt-2 text-zinc-300"
                >
                  <p>  <span className="text-green-400">✓</span> tool/calculator responds correctly (12ms)</p>
                  <p>  <span className="text-green-400">✓</span> resource/docs returns valid schema (8ms)</p>
                  <p>  <span className="text-green-400">✓</span> prompt/greeting handles params (15ms)</p>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.5 }}
                  className="text-zinc-500 mt-4 text-xs"
                >
                  Tests: 3 passed | Time: 0.035s
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </section>

        {/* The Problem Landscape */}
        <section className="py-20 px-8 bg-zinc-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
                The World Changed Fast
              </h2>
              <p className="font-lora text-lg text-zinc-600 max-w-2xl">
                In late 2024, Anthropic released MCP. Within months, it became the de-facto standard for AI agent tooling. But one critical piece was missing.
              </p>
            </motion.div>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-zinc-300 transform md:-translate-x-1/2" />

              {timelineEvents.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center mb-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  onMouseEnter={() => setActiveTimeline(index)}
                  onMouseLeave={() => setActiveTimeline(null)}
                >
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'} pl-8 md:pl-0`}>
                    <div
                      className={`p-6 rounded-lg transition-all duration-300 cursor-pointer ${
                        event.highlight ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200'
                      } ${activeTimeline === index ? 'transform scale-105 shadow-lg' : ''}`}
                    >
                      <span className={`text-xs font-inter uppercase tracking-wider ${
                        event.highlight ? 'text-zinc-400' : 'text-zinc-500'
                      }`}>
                        {event.date}
                      </span>
                      <h3 className={`font-inter font-semibold text-lg mt-1 ${
                        event.highlight ? 'text-white' : 'text-zinc-900'
                      }`}>
                        {event.title}
                      </h3>
                      <p className={`font-lora text-sm mt-2 ${
                        event.highlight ? 'text-zinc-300' : 'text-zinc-600'
                      }`}>
                        {event.description}
                      </p>
                    </div>
                  </div>

                  {/* Timeline dot */}
                  <div className={`absolute left-0 md:left-1/2 w-4 h-4 rounded-full transform md:-translate-x-1/2 ${
                    event.highlight ? 'bg-zinc-900' : 'bg-zinc-400'
                  } ${activeTimeline === index ? 'scale-150' : ''} transition-transform`} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* The Insight */}
        <section className="py-20 px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="font-inter font-bold text-3xl md:text-4xl text-zinc-900 mb-8">
                    The Insight
                  </h2>

                  <div className="font-lora text-lg text-zinc-700 space-y-6 leading-relaxed">
                    <p>
                      I spent weeks watching developers struggle. The pattern was always the same:
                      they'd build an MCP server, manually test it with Claude, ship it, and pray.
                    </p>

                    <p>
                      The existing tools weren't designed for this. MCP Inspector was great for debugging
                      but couldn't run in CI/CD. Postman didn't understand MCP semantics. JMeter was overkill.
                    </p>

                    <div className="bg-zinc-100 border-l-4 border-zinc-900 p-6 my-8">
                      <p className="font-inter font-semibold text-zinc-900 text-xl">
                        "What if testing MCP servers could feel exactly like testing JavaScript?"
                      </p>
                    </div>

                    <p>
                      Jest had solved this problem for JavaScript years ago. Developers loved it because
                      it was familiar, fast, and just worked. Why reinvent the wheel?
                    </p>

                    <h3 className="font-inter font-semibold text-xl text-zinc-900 mt-12 mb-4">
                      Design Principles
                    </h3>

                    <ul className="space-y-4">
                      {[
                        { title: "JSON-first configuration", desc: "No learning curve, just data" },
                        { title: "Transport-agnostic", desc: "stdio, SSE, HTTP - all work the same" },
                        { title: "Parallel by default", desc: "Fast feedback, always" },
                        { title: "Snapshot testing", desc: "Catch regressions automatically" },
                        { title: "CI/CD native", desc: "Built for automation from day one" },
                      ].map((principle, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.1 }}
                          className="flex items-start gap-4"
                        >
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-inter font-semibold text-zinc-900">{principle.title}</span>
                            <span className="text-zinc-600"> - {principle.desc}</span>
                          </div>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </div>

              {/* Marginalia */}
              <aside className="lg:col-span-4 space-y-8">
                {[
                  "Developers don't want to learn a new testing paradigm just for MCP.",
                  "The best tools feel invisible. They get out of your way.",
                  "CI/CD integration isn't a feature, it's the foundation.",
                  "JSON removes the anxiety of learning yet another config language.",
                ].map((quote, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className="p-6 border-l-2 border-zinc-300 hover:border-zinc-900 transition-colors"
                    style={{ transform: `translateY(${Math.sin((scrollY + index * 100) * 0.002) * 5}px)` }}
                  >
                    <p className="font-lora italic text-zinc-600 text-sm leading-relaxed">
                      "{quote}"
                    </p>
                  </motion.div>
                ))}
              </aside>
            </div>
          </div>
        </section>

        {/* Before/After Comparison */}
        <section className="py-20 px-8 bg-zinc-900 text-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl mb-4">
                Before & After
              </h2>
              <p className="font-lora text-lg text-zinc-400">
                The difference between testing manually and testing with mcp-jest
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Before */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-zinc-800 rounded-lg p-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <h3 className="font-inter font-semibold text-xl">Before mcp-jest</h3>
                </div>

                <div className="space-y-4 font-mono text-sm">
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-zinc-500 mt-1" />
                    <div>
                      <p className="text-zinc-400">1. Start your MCP server manually</p>
                      <p className="text-zinc-600 text-xs">$ node server.js</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-zinc-500 mt-1" />
                    <div>
                      <p className="text-zinc-400">2. Open Claude Desktop</p>
                      <p className="text-zinc-600 text-xs">Configure claude_desktop_config.json</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-zinc-500 mt-1" />
                    <div>
                      <p className="text-zinc-400">3. Manually invoke each tool</p>
                      <p className="text-zinc-600 text-xs">"Hey Claude, try the calculator tool..."</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-zinc-500 mt-1" />
                    <div>
                      <p className="text-zinc-400">4. Eyeball the response</p>
                      <p className="text-zinc-600 text-xs">"Does this look right?"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-zinc-500 mt-1" />
                    <div>
                      <p className="text-zinc-400">5. Repeat for every change</p>
                      <p className="text-zinc-600 text-xs">And hope you didn't miss anything</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-zinc-700">
                  <p className="text-zinc-500 text-sm">
                    <span className="text-red-400 font-semibold">~15 minutes</span> per test cycle
                  </p>
                  <p className="text-zinc-600 text-xs mt-1">No CI/CD. No snapshots. No confidence.</p>
                </div>
              </motion.div>

              {/* After */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-900/50 to-zinc-800 rounded-lg p-8 border border-green-800"
              >
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <h3 className="font-inter font-semibold text-xl">With mcp-jest</h3>
                </div>

                <div className="space-y-4 font-mono text-sm">
                  <div>
                    <p className="text-zinc-400 mb-2">1. Write your test (JSON):</p>
                    <pre className="bg-zinc-900 rounded p-4 text-xs overflow-x-auto">
{`{
  "server": "node server.js",
  "tests": [{
    "type": "tool",
    "name": "calculator",
    "input": { "a": 5, "b": 3 },
    "expect": { "result": 8 }
  }]
}`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-zinc-400 mb-2">2. Run it:</p>
                    <p className="text-green-400">$ mcp-jest tests/</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 mb-2">3. Done.</p>
                    <p className="text-green-400">PASS  All tests passed (0.035s)</p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-green-800">
                  <p className="text-zinc-400 text-sm">
                    <span className="text-green-400 font-semibold">~10 seconds</span> per test cycle
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">CI/CD ready. Snapshots. Confidence.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Competitive Landscape */}
        <section className="py-20 px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
                The Landscape
              </h2>
              <p className="font-lora text-lg text-zinc-600">
                Why existing tools weren't enough
              </p>
            </motion.div>

            <div className="space-y-4">
              {competitorData.map((competitor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white border border-zinc-200 rounded-lg p-6 hover:border-zinc-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{competitor.icon}</span>
                      <div>
                        <h3 className="font-inter font-semibold text-zinc-900">{competitor.name}</h3>
                        <p className="text-sm text-zinc-500">Focus: {competitor.focus}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs rounded-full">
                        {competitor.limitation}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* mcp-jest highlight */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-zinc-900 text-white rounded-lg p-6 mt-8"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <h3 className="font-inter font-semibold">mcp-jest</h3>
                      <p className="text-sm text-zinc-400">Focus: Complete MCP Testing</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-block px-3 py-1 bg-green-900 text-green-400 text-xs rounded-full">
                      Fills the gap
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 px-8 bg-zinc-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
                Impact
              </h2>
              <p className="font-lora text-lg text-zinc-600">
                Numbers that matter
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: "500+", label: "Weekly Downloads", icon: Package },
                { value: githubStats?.stars?.toString() || "708", label: "GitHub Stars", icon: Github },
                { value: "10s", label: "Avg Test Time", icon: Zap },
                { value: "MIT", label: "Licensed", icon: CheckCircle2 },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 text-center border border-zinc-200 hover:border-zinc-400 hover:shadow-lg transition-all"
                >
                  <stat.icon className="w-6 h-6 text-zinc-400 mx-auto mb-3" />
                  <p className="font-inter font-black text-3xl text-zinc-900">{stat.value}</p>
                  <p className="text-sm text-zinc-500 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl text-zinc-900 mb-8">
                The Larger Picture
              </h2>

              <div className="font-lora text-lg text-zinc-600 space-y-6 leading-relaxed">
                <p>
                  MCP isn't just another protocol. It's becoming the universal language between
                  AI agents and the world. As agents go to production, testing infrastructure
                  becomes critical.
                </p>
                <p>
                  mcp-jest is just the beginning. The vision is a complete testing ecosystem:
                  security scanning, performance profiling, visual test builders.
                </p>
              </div>

              <div className="mt-16 p-8 bg-zinc-100 rounded-lg">
                <blockquote className="font-lora text-2xl md:text-3xl text-zinc-800 italic leading-relaxed">
                  "In a world where AI writes code, the craft isn't writing code yourself.
                  it's building the tools that ensure the code works."
                </blockquote>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-8 bg-zinc-900 text-white">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-inter font-bold text-3xl md:text-4xl mb-8">
                Try It Now
              </h2>

              <div className="bg-zinc-800 rounded-lg p-6 mb-8 font-mono">
                <code className="text-green-400 text-lg">npm install -g mcp-jest</code>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://mcp-jest.ddiy.diy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 rounded-lg font-inter font-semibold hover:bg-zinc-100 transition-colors"
                >
                  Read the Docs
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href="https://github.com/josharsh/mcp-jest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white rounded-lg font-inter font-semibold hover:bg-zinc-700 transition-colors border border-zinc-700"
                >
                  <Github className="w-4 h-4" />
                  View on GitHub
                </a>
              </div>

              <p className="mt-12 text-zinc-500 text-sm font-inter">
                Built by <a href="/" className="text-white hover:underline">Harsh Joshi</a>
              </p>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="mcp-jest" variant="light" />
    </div>
  );
};

export default McpJestCaseStudy;
